import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactElement;
  display?: boolean;
}

const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  display = true,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [showAbove, setShowAbove] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const updateTooltipPosition = () => {
    if (triggerRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipHeight = tooltipRef.current?.offsetHeight || 0;

      // Check if there's enough space below
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - triggerRect.bottom;
      const needsFlip = spaceBelow < tooltipHeight + 10; // Add some margin

      setShowAbove(needsFlip);

      setPosition({
        top: needsFlip
          ? triggerRect.top + window.scrollY - tooltipHeight - 10 // Position above with margin
          : triggerRect.bottom + window.scrollY + 10, // Position below with margin
        left: triggerRect.left + window.scrollX + triggerRect.width / 2,
      });
    }
  };

  useEffect(() => {
    if (isVisible) {
      // Initial position
      updateTooltipPosition();

      // Update position after the tooltip is rendered to get accurate dimensions
      setTimeout(updateTooltipPosition, 0);
    }
  }, [isVisible]);

  if (!display) return <>{children}</>;

  return (
    <div
      ref={triggerRef}
      className="group inline-block"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}

      {isVisible &&
        createPortal(
          <div
            ref={tooltipRef}
            className="fixed z-50 transform -translate-x-1/2 bg-gray-800 text-white text-sm rounded py-1 px-2 max-w-xs whitespace-pre-wrap break-words"
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
            }}
          >
            {content}
          </div>,
          document.body
        )}
    </div>
  );
};

export default Tooltip;
